import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ButtonItem from '../components/ButtonItem';

const TYPES = ['CD', 'LP', '굿즈'];

function PhysicalAlbumModifyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const page = params.get('page') || 1;

  const [form, setForm] = useState(null);
  const [albumSearch, setAlbumSearch] = useState('');
  const [albumResults, setAlbumResults] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`/api/physical-albums/${id}`)
      .then((res) => {
        const item = res.data;
        setForm({
          albumId: item.albumId,
          type: item.type || 'CD',
          barcode: item.barcode || '',
          label: item.label || '',
          returnDeposit: item.returnDeposit ?? '',
          supplyPrice: item.supplyPrice || '',
          stock: item.stock ?? '',
          feeRate: item.feeRate || '',
        });
        setSelectedAlbum(item.album);
        setAlbumSearch(item.album?.nameKr || '');
      })
      .catch(() => navigate('/PhysicalAlbums'));
  }, [id, navigate]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!albumSearch || selectedAlbum) return setAlbumResults([]);
      axios.get('/album', { params: { search: albumSearch, page: 1, limit: 10 } })
        .then((res) => setAlbumResults(res.data.items || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [albumSearch, selectedAlbum]);

  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.albumId) return alert('앨범을 선택해주세요.');
    setSaving(true);
    try {
      await axios.put(`/api/physical-albums/${id}`, form);
      navigate(`/PhysicalAlbums/${id}?page=${page}`);
    } catch (e) {
      alert(e.response?.data?.errorMessage || '저장 오류');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500" />
    </div>
  );

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">
          <span>음반 수정</span>
        </div>
      </div>

      <div className="space-y-6 max-w-2xl mx-auto">

        {/* 앨범 검색 */}
        <div>
          <label className="block font-semibold mb-1">앨범 <span className="text-rose-400">*</span></label>
          <input
            value={albumSearch}
            onChange={(e) => { setAlbumSearch(e.target.value); setSelectedAlbum(null); setForm((p) => ({ ...p, albumId: '' })); }}
            placeholder="앨범명 검색..."
            className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500"
          />
          {albumResults.length > 0 && !selectedAlbum && (
            <div className="mt-1 bg-[#1a2740] border border-white/10 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
              {albumResults.map((a) => (
                <button key={a.id}
                  onClick={() => { setSelectedAlbum(a); setAlbumSearch(a.nameKr); setForm((p) => ({ ...p, albumId: a.id })); setAlbumResults([]); }}
                  className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/10 flex items-center gap-2">
                  {a.image && <img src={a.image} className="w-6 h-6 rounded object-cover" alt="" />}
                  <span>{a.nameKr}</span>
                  <span className="text-slate-500 text-xs ml-auto">{a.artist?.nameKr}</span>
                </button>
              ))}
            </div>
          )}
          {selectedAlbum && <div className="mt-1 text-xs text-sky-400">{selectedAlbum.nameKr} 선택됨</div>}
        </div>

        {/* 분류 */}
        <div>
          <label className="block font-semibold mb-1">분류 <span className="text-rose-400">*</span></label>
          <div className="flex gap-2">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setForm((p) => ({ ...p, type: t }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${form.type === t ? 'bg-sky-600 border-sky-500 text-white' : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* 기획사 / 바코드 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">기획사</label>
            <input value={form.label} onChange={f('label')} placeholder="기획사"
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500" />
          </div>
          <div>
            <label className="block font-semibold mb-1">바코드</label>
            <input value={form.barcode} onChange={f('barcode')} placeholder="바코드"
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500" />
          </div>
        </div>

        {/* 출고가 / 반품예치금 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">출고가 (원)</label>
            <input type="number" value={form.supplyPrice} onChange={f('supplyPrice')} placeholder="0"
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500" />
          </div>
          <div>
            <label className="block font-semibold mb-1">반품예치금 (원)</label>
            <input type="number" value={form.returnDeposit} onChange={f('returnDeposit')} placeholder="0"
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500" />
          </div>
        </div>

        {/* 재고 / 수수료율 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">재고</label>
            <input type="number" value={form.stock} onChange={f('stock')} placeholder="0"
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500" />
          </div>
          <div>
            <label className="block font-semibold mb-1">유통수수료율 (%)</label>
            <input type="number" value={form.feeRate} onChange={f('feeRate')} placeholder="0"
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500" />
          </div>
        </div>

      </div>

      <div className="w-full flex justify-end items-center mt-10 gap-2">
        <ButtonItem text="목록" color="gray" onClick={() => navigate(`/PhysicalAlbums?page=${page}`)} />
        <ButtonItem text="저장" color="blue" onClick={handleSave} disabled={saving} />
      </div>
    </>
  );
}

export default PhysicalAlbumModifyPage;
